import { useTranslation } from "react-i18next";
import type { ProfessionalAppointmentTypeBreakdown } from "@/hooks/useReportsByProfessional";
import { getAppointmentTypeIcon, renderIconBySlug } from "@/lib/event-icons";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface AppointmentTypesPanelProps {
  rows: ProfessionalAppointmentTypeBreakdown[];
  isLoading: boolean;
  formatCurrency: (v: number) => string;
}

function pctBadgeVariant(pct: number): "default" | "secondary" | "destructive" {
  if (pct >= 95) return "default";
  if (pct >= 80) return "secondary";
  return "destructive";
}

export function AppointmentTypesPanel({ rows, isLoading, formatCurrency }: AppointmentTypesPanelProps) {
  const { t } = useTranslation("reports");

  const sorted = [...rows].sort((a, b) => b.totalAppointments - a.totalAppointments);
  const maxTotal = sorted[0]?.totalAppointments ?? 1;
  const loadingRows = Array.from({ length: 4 }, (_, index) => ({
    type: `loading-${index}`,
    typeLabel: "–",
    color: null,
    icon: null,
    totalAppointments: 1,
    confirmados: 0,
    finalizados: 0,
    pagos: 0,
    receita: 0,
    ticketMedio: 0,
    pctFinalizados: 0,
    pctPagos: 0,
  }));
  const displayRows = isLoading ? loadingRows : sorted;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("appointmentTypesTitle")}</CardTitle>
        <CardDescription>{t("appointmentTypesDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        {!isLoading && sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">{t("emptyBreakdown")}</p>
        ) : (
          <div className="space-y-3">
            {displayRows.map((row) => {
              const paidPct = row.totalAppointments > 0 ? (row.pagos / row.totalAppointments) * 100 : 0;
              const totalBarW = maxTotal > 0 ? (row.totalAppointments / maxTotal) * 100 : 0;
              const paidBarW = row.totalAppointments > 0 ? (row.pagos / row.totalAppointments) * 100 : 0;
              const typeColor = isLoading ? "hsl(var(--muted))" : row.color ?? "hsl(var(--primary))";
              const icon = !isLoading && row.icon
                ? renderIconBySlug(row.icon, { tone: "current", sizeClass: "h-3.5 w-3.5" })
                : getAppointmentTypeIcon(row.type, { tone: "current", sizeClass: "h-3.5 w-3.5" });

              return (
                <div key={row.type} className="grid grid-cols-[minmax(6.5rem,8rem)_minmax(4rem,1fr)_2.75rem_2.25rem] items-center gap-2 sm:grid-cols-[minmax(8rem,9rem)_minmax(6rem,1fr)_3.25rem_3rem_5rem] sm:gap-3">
                  {/* Name + type identity */}
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white"
                        style={{ backgroundColor: typeColor }}
                      >
                        {icon}
                      </span>
                      <p className="truncate text-xs font-medium">{isLoading ? "–" : row.typeLabel}</p>
                    </div>
                  </div>

                  {/* Bar: total (translucent) + paid (solid) */}
                  <div className="flex-1 h-3 rounded-full overflow-hidden relative bg-muted">
                    <div
                      className="absolute left-0 top-0 h-full rounded-full opacity-30"
                      style={{ backgroundColor: typeColor, width: `${totalBarW}%` }}
                    />
                    <div
                      className="absolute left-0 top-0 h-full rounded-full"
                      style={{
                        backgroundColor: typeColor,
                        width: `${Math.max(isLoading ? 100 : 0, (paidBarW / 100) * totalBarW)}%`,
                      }}
                    />
                  </div>

                  {/* Paid / Total */}
                  <div className="shrink-0 text-xs tabular-nums text-right w-14 text-muted-foreground">
                    {isLoading ? "–" : `${row.pagos}/${row.totalAppointments}`}
                  </div>

                  {/* % badge */}
                  <Badge
                    variant={isLoading ? "secondary" : pctBadgeVariant(paidPct)}
                    className="hidden sm:block shrink-0 text-[10px] px-1.5 py-0 h-5 tabular-nums"
                  >
                    {isLoading ? "–" : `${paidPct.toFixed(0)}%`}
                  </Badge>

                  {/* Revenue */}
                  <div className="hidden shrink-0 text-xs font-semibold tabular-nums text-right sm:block">
                    {isLoading ? "–" : formatCurrency(row.receita)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
